import React, { useContext, useEffect, useMemo, useState } from 'react';
import './AdminProducts.css'; // reuse existing styles instead of missing ./AdminOrders.css
import { LocaleContext, AuthContext } from '../locale';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:4000/api';

export default function AdminOrders() {
  const { locale } = useContext(LocaleContext);
  const { user, token, openAuth } = useContext(AuthContext);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [detailOrder, setDetailOrder] = useState(null);
  const [detailItems, setDetailItems] = useState([]);
  const [savingId, setSavingId] = useState(null);

  const nf = useMemo(
    () => new Intl.NumberFormat(locale === 'fr' ? 'fr-FR' : 'en-US', { style: 'currency', currency: 'EUR' }),
    [locale]
  );

  // Protect
  useEffect(() => {
    if (!user) {
      openAuth && openAuth('login');
      window.location.hash = '#/';
    } else if (user.role !== 'admin') {
      window.location.hash = '#/';
    }
  }, [user, openAuth]);

  const fetchOrders = async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/payments/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to fetch orders');
      setOrders(Array.isArray(data.orders) ? data.orders : []);
    } catch (e) {
      setError(e.message || 'Error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return orders;
    return orders.filter(o =>
      [
        o.id,
        o.user_email,
        o.payment_status,
        o.payment_method,
        o.customer_name,
        o.customer_email
      ]
        .filter(Boolean)
        .some(v => String(v).toLowerCase().includes(q))
    );
  }, [orders, search]);

  const methodLabel = (m) => (m === 'cod' ? 'Livraison ' : 'Stripe');
  const FULFILL_OPTIONS = [
    { value: 'ordered', label: 'Commandée' },
    { value: 'shipped', label: 'Expédiée' },
    { value: 'delivered', label: 'Livrée' } // NEW
  ];

  const updateFulfillment = async (orderId, next) => {
    if (!token) return;
    setSavingId(orderId);
    const prev = orders.find(o => o.id === orderId)?.fulfillment_status || 'ordered';
    // optimistic UI update
    setOrders(os => os.map(o => o.id === orderId ? { ...o, fulfillment_status: next } : o));
    try {
      const res = await fetch(`${API_BASE}/payments/orders/${orderId}/fulfillment`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status: next })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to update');
    } catch (e) {
      // revert on error
      setOrders(os => os.map(o => o.id === orderId ? { ...o, fulfillment_status: prev } : o));
      alert(e.message || 'Erreur lors de la mise à jour du statut');
    } finally {
      setSavingId(null);
    }
  };

  const openDetails = async (id) => {
    if (!token) return;
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailError('');
    setDetailOrder(null);
    setDetailItems([]);
    try {
      const res = await fetch(`${API_BASE}/payments/orders/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to load order');
      setDetailOrder(data.order);
      setDetailItems(Array.isArray(data.items) ? data.items : []);
    } catch (e) {
      setDetailError(e.message || 'Error loading order');
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetails = () => {
    setDetailOpen(false);
    setDetailOrder(null);
    setDetailItems([]);
    setDetailError('');
  };

  const renderAddress = (shipping_address) => {
    if (!shipping_address) return '—';
    let obj = shipping_address;
    if (typeof obj === 'string') {
      try { obj = JSON.parse(obj); } catch { return obj; }
    }
    // Stripe shape: { name, address: { line1, line2, city, postal_code, country } }
    const addr = obj.address || obj;
    if (addr.line1 || addr.city || addr.postal_code || addr.country) {
      return [
        addr.line1,
        addr.line2,
        [addr.postal_code, addr.city].filter(Boolean).join(' '),
        addr.country
      ].filter(Boolean).join(', ');
    }
    if (obj.raw) return obj.raw;
    return '—';
  };

  if (!user || user.role !== 'admin') return null;

  return (
    <section className="admin-orders">
      <div className="container">
        <div className="page-header">
          <div className="header-content">
            <button
              className="back-btn"
              onClick={() => (window.location.hash = '#/admin')}
              title="Retour au dashboard"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
            </button>
            <div className="header-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
              </svg>
            </div>
            <div className="header-text">
              <h1 className="h1">Commandes</h1>
              <p className="page-subtitle">{orders.length} commande(s)</p>
            </div>
          </div>
          <div className="header-actions">
            <input
              className="search-input"
              type="text"
              placeholder="Rechercher par e‑mail, méthode..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button className="btn secondary" onClick={fetchOrders} disabled={loading} title="Rafraîchir">
              {loading ? (
                <>
                  <div className="spinner" />
                  Chargement...
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 8 }}>
                    <polyline points="23 4 23 10 17 10" />
                    <polyline points="1 20 1 14 7 14" />
                    <path d="M3.51 9a9 9 0 0 1 14.13-3.36L23 10" />
                    <path d="M20.49 15a9 9 0 0 1-14.13 3.36L1 14" />
                  </svg>
                  Rafraîchir
                </>
              )}
            </button>
          </div>
        </div>

        <div className="orders-content">
          {error && <div className="alert error">{error}</div>}
          {loading ? (
            <div className="centered">
              <div className="spinner-lg" />
              <span>Chargement des commandes...</span>
            </div>
          ) : filtered.length === 0 ? (
            <div className="empty">
              <div className="empty-icon">📦</div>
              <h3>Aucune commande</h3>
              <p>Aucune commande trouvée pour votre recherche.</p>
            </div>
          ) : (
            <div className="table-wrapper">
              <table className="products-table">
                <thead>
                  <tr>
                    <th style={{width: '80px'}}>#</th>
                    <th style={{width: '140px'}}>Date</th>
                    <th style={{width: '120px'}}>Client</th>
                    <th style={{width: '180px'}}>Email</th>
                    <th style={{width: '150px'}}>Méthode</th>
                    <th style={{width: '130px'}}>Expédition</th>
                    <th style={{width: '100px'}}>Total</th>
                    <th style={{width: '80px'}}>Frais</th>
                    <th style={{width: '70px'}}>Articles</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((o) => {
                    const methodBadge = `badge method-${o.payment_method}`;
                    const date = o.created_at ? new Date(o.created_at).toLocaleString(locale === 'fr' ? 'fr-FR' : 'en-US') : '-';
                    const methodText = o.payment_method === 'cod' ? 'Livraison' : 'Stripe';
                    const fulfill = o.fulfillment_status || 'ordered';
                    return (
                      <tr
                        key={o.id}
                        className="clickable-row"
                        onClick={() => openDetails(o.id)}
                        title="Voir les détails de la commande"
                      >
                        <td className="cell-strong">#{o.id}</td>
                        <td className="cell-muted">{date}</td>
                        <td>{o.customer_name || '—'}</td>
                        <td className="cell-muted">{o.customer_email || o.user_email}</td>
                        <td><span className={methodBadge}>{methodText}</span></td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <select
                            value={fulfill}
                            onChange={(e) => updateFulfillment(o.id, e.target.value)}
                            disabled={savingId === o.id}
                            className="fulfillment-select"
                            aria-label="Statut d'expédition"
                          >
                            {FULFILL_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </td>
                        <td className="cell-strong">{nf.format(Number(o.total_amount || 0))}</td>
                        <td className="cell-muted">{nf.format(Number(o.shipping_amount || 0))}</td>
                        <td className="cell-muted">{o.items_count || 0}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Details modal */}
      {detailOpen && (
        <div className="modal-backdrop" onClick={closeDetails}>
          <div className="modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Détails de la commande {detailOrder ? `#${detailOrder.id}` : ''}</h3>
              <button className="modal-close" onClick={closeDetails} aria-label="Fermer">✕</button>
            </div>

            {detailLoading ? (
              <div className="modal-body centered"><div className="spinner-lg" /> Chargement...</div>
            ) : detailError ? (
              <div className="modal-body"><div className="alert error">{detailError}</div></div>
            ) : detailOrder ? (
              <div className="modal-body">
                <div className="order-summary-grid">
                  <div className="os-card">
                    <div className="os-row"><span>Commande</span><strong>#{detailOrder.id}</strong></div>
                    <div className="os-row"><span>Date</span><strong>{detailOrder.created_at ? new Date(detailOrder.created_at).toLocaleString(locale === 'fr' ? 'fr-FR' : 'en-US') : '-'}</strong></div>
                    <div className="os-row"><span>Méthode</span><strong>{methodLabel(detailOrder.payment_method)}</strong></div>
                  </div>
                  <div className="os-card">
                    <div className="os-row"><span>Client</span><strong>{detailOrder.customer_name || '—'}</strong></div>
                    <div className="os-row"><span>Email</span><strong>{detailOrder.customer_email || detailOrder.user_email || '—'}</strong></div>
                    <div className="os-row"><span>Adresse</span><strong>{renderAddress(detailOrder.shipping_address)}</strong></div>
                  </div>
                  <div className="os-card">
                    <div className="os-row"><span>Sous‑total</span><strong>{nf.format(Number(detailOrder.total_amount || 0))}</strong></div>
                    <div className="os-row"><span>Livraison</span><strong>{nf.format(Number(detailOrder.shipping_amount || 0))}</strong></div>
                    <div className="os-row total"><span>Total</span><strong>{nf.format(Number(detailOrder.total_amount || 0) + Number(detailOrder.shipping_amount || 0))}</strong></div>
                  </div>
                </div>

                <div className="items-list">
                  <h4>Articles</h4>
                  {detailItems.length === 0 ? (
                    <div className="empty">Aucun article</div>
                  ) : (
                    <ul>
                      {detailItems.map(it => {
                        const price = Number(it.price || 0);
                        const qty = Number(it.quantity || 1);
                        return (
                          <li key={it.id || `${it.product_id}-${it.name}`}>
                            <div className="item-line">
                              <div className="il-name">{it.name}</div>
                              <div className="il-qty">× {qty}</div>
                              <div className="il-price">{nf.format(price)}</div>
                              <div className="il-total">{nf.format(price * qty)}</div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </section>
  );
}
